import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { Store } from '@ngxs/store';
import { ConnectionStatus, Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Deploy } from 'cordova-plugin-ionic/dist/ngx';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { NgIdleModule } from '@ng-idle/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxActioncableService } from '@csventures/ngx-actioncable';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsResetPluginModule } from 'ngxs-reset-plugin';
import { NgxsSelectSnapshotModule } from '@ngxs-labs/select-snapshot';
import { RouteReuseStrategy } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { TranslocoLocaleModule } from '@ngneat/transloco-locale';
import 'moment/locale/fr';
import 'moment/locale/es';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { HeadersInterceptor } from './interceptors/headers.interceptor';
import { PosManagerMenuModule } from './components/pos-manager/menu/pos-manager.module';
import { SharedModule } from './components/shared/shared.module';
import { StoreModule } from './store/store.module';
import { TimeoutModal } from './modals/timeout/timeout.modal';
import { UrlInterceptor } from './interceptors/url.interceptor';
import { environment } from 'src/environments/environment';
import { getErrorHandler } from './sentry-error-handler';
import { BlurButtonDirectiveModule } from 'src/app/directives/blur-button/blur-button-directive.module';
import { InputFocusDirectiveModule } from 'src/app/directives/input-focus-directive/input-focus-directive.module';
import { MenuModule } from 'src/app/components/services-appointment/menu';
import {
  LoadHardwareDetails,
  NetworkStatusChange
} from 'src/app/store/app/app.action';
import { NfcReaderService } from 'src/app/services/nfc-reader/nfc-reader.service';
import { ConfirmModalModule } from 'src/app/modals/confirm/confirm-modal.module';
import { RefundsModalModule } from 'src/app/modals/refunds/refunds-modal.module';
import { OrderSetupModalModule } from 'src/app/modals/order-setup/order-setup-modal.module';
import { ManagerApprovalModalModule } from 'src/app/modals/manager-approval/manager-approval-modal.module';
import { GiftCardPaymentModalModule } from 'src/app/modals/payments/gift-card/gift-card-payment-modal.module';
import { DinerAssignmentModalModule } from 'src/app/modals/ticket/diner-assignment/diner-assignment-modal.module';
import { PatientInformationModalModule } from 'src/app/modals/patient-information/patient-information-modal.module';
import { EditTableModalModule } from 'src/app/modals/dining/edit-table/edit-table-modal.module';
import { TableAssignmentSearchStaffModalModule } from 'src/app/modals/dining/table-assignment-search-staff/table-assignment-search-staff-modal.module';
import { BillToRoomPaymentModalModule } from 'src/app/modals/payments/bill-to-room/bill-to-room-payment-modal.module';
import { GatewayTransactionModalModule } from 'src/app/modals/payments/gateway-transaction/gateway-transaction-modal.module';
import { DepartmentChargeAccountPaymentModalModule } from 'src/app/modals/payments/department-charge-account/department-charge-account-payment-modal.module';
import { PosManagerConfirmModalModule } from 'src/app/modals/pos-manager/modifier-prompts/pos-manager-confirm/pos-manager-confirm-modal.module';
import { ModifierPromptsAssignmentModalModule } from 'src/app/modals/pos-manager/modifier-prompts-assignment/modifier-prompts-assignment-modal.module';
import { AddItemModifierPromptsModalModule } from 'src/app/modals/item-modifier-prompts/add-item-modifier-prompts-modal.module';
import { CustomPriceItemModalModule } from 'src/app/modals/macro-grid/custom-price-item/custom-price-item-modal.module';
import { WeightedItemModalModule } from 'src/app/modals/macro-grid/weighted-item/weighted-item-modal.module';
import { GiftCardItemModalModule } from 'src/app/modals/macro-grid/gift-card-item/gift-card-item-modal.module';
import { OpenTextModifierModalModule } from 'src/app/modals/macro-grid/open-text-modifier/open-text-modifier-modal.module';
import { SelfServiceGatewayTransactionModalModule } from 'src/app/components/self-service/modals/self-service-gateway-transaction/self-service-gateway-transaction-modal.module';
import { SelfServiceInputSecureCodeModalModule } from 'src/app/components/self-service/modals/self-service-input-secure-code/self-service-input-secure-code-modal.module';
import { PosLoadingModalModule } from 'src/app/components/shared/loading-modal';
import { ChargeGuestMealModalModule } from 'src/app/modals/charge-guest-meal/charge-guest-meal-modal.module';
import { StripeTransactionModalModule } from 'src/app/modals/payments/stripe-transaction/stripe-transaction-modal.module';
import { SelectResidentForGuestBillingModule } from 'src/app/modals/select-resident-for-guest-billing/select-resident-for-guest-billing.module';
import { SskNoteModalModule } from 'src/app/pages/self-service/modals/ssk-note-modal';
import { CtdUnpaidModalComponentModule } from 'src/app/modals/pos-manager/ctd-unpaid-modal/ctd-unpaid-modal.module';
import { ProductionAreaOverrideModalModule } from './modals/pos-manager/production-area-override/production-area-override-modal.module';
import { RealtimeInfoMenuModule } from './pages/pos-manager/realtime-info/realtime-info-menu.module';
import { MostPopularItemsComponentModule } from './components/pos-manager/most-popular-items/most-popular-items.component.module';
import { TranslocoRootModule } from './transloco.config';
const MODAL_MODULES = [
  ChargeGuestMealModalModule,
  RefundsModalModule,
  OrderSetupModalModule,
  GiftCardPaymentModalModule,
  ManagerApprovalModalModule,
  DinerAssignmentModalModule,
  SelectResidentForGuestBillingModule,
  ConfirmModalModule,
  PatientInformationModalModule,
  EditTableModalModule,
  TableAssignmentSearchStaffModalModule,
  BillToRoomPaymentModalModule,
  GatewayTransactionModalModule,
  DepartmentChargeAccountPaymentModalModule,
  PosManagerConfirmModalModule,
  ModifierPromptsAssignmentModalModule,
  RealtimeInfoMenuModule,
  ProductionAreaOverrideModalModule,
  AddItemModifierPromptsModalModule,
  CustomPriceItemModalModule,
  WeightedItemModalModule,
  GiftCardItemModalModule,
  OpenTextModifierModalModule,
  SelfServiceGatewayTransactionModalModule,
  SelfServiceInputSecureCodeModalModule,
  StripeTransactionModalModule,
  PosLoadingModalModule,
  SskNoteModalModule,
  MostPopularItemsComponentModule,
  CtdUnpaidModalComponentModule
];

const MENU_MODULES = [];

function initializeApp(store: Store, nfc: NfcReaderService) {
  return () =>
    new Promise(async (resolve) => {
      const isAndroid = Capacitor.getPlatform() === 'android';
      let nfcEnabled = false;

      //Some Android devices will crash if NFC is not on the device or disabled, wrapping this logic on the try statement to avoid it
      try {
        //If platform is Android AND NFC is Enabled on the Device
        if (isAndroid) {
          nfcEnabled = (await nfc.isNFCEnabled()).success;
        }
      } catch (error) {
        // Error when the device doesn't support NFC.
        // Catch exception for reducing Sentry issues
      }

      // Do some asynchronous stuff
      const deviceInfo = await Device.getInfo();
      const deviceId = await Device.getId();

      store.dispatch(new LoadHardwareDetails(deviceInfo, deviceId, nfcEnabled));

      await Network.addListener(
        'networkStatusChange',
        (network: ConnectionStatus) =>
          store.dispatch(new NetworkStatusChange(network.connected))
      );

      await SplashScreen.hide();

      resolve(null);
    });
}

@NgModule({
  declarations: [AppComponent, TimeoutModal],
  entryComponents: [],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    StoreModule,
    SharedModule,
    NgSelectModule,
    PosManagerMenuModule,
    MatMomentDateModule,
    BrowserAnimationsModule,
    FontAwesomeModule,
    BlurButtonDirectiveModule,
    InputFocusDirectiveModule,
    TranslocoRootModule,
    TranslocoLocaleModule.forRoot({
      langToLocaleMapping: {
        en: 'en-US',
        fr: 'fr-FR',
        es: 'es-ES'
      }
    }),
    MenuModule,
    IonicModule.forRoot(),
    NgxsResetPluginModule.forRoot(),
    NgxsSelectSnapshotModule.forRoot(),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    NgIdleModule.forRoot(),
    NgxsLoggerPluginModule.forRoot({
      disabled: environment.MSPOS_IS_DEPLOYED_BUILD
    }),
    ...MODAL_MODULES,
    ...MENU_MODULES
  ],
  providers: [
    NfcReaderService,
    NgxActioncableService,
    Deploy,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: UrlInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HeadersInterceptor, multi: true },
    { provide: ErrorHandler, useFactory: getErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      multi: true,
      deps: [Store, NfcReaderService]
    }
  ],
  bootstrap: [AppComponent],
  schemas: []
})
export class AppModule {}
